import { z } from 'zod';
import { MAX_UPLOAD_FILES, UPLOAD_REQUEST_TARGET_TYPES } from '../types/messaging.js';
import { MAX_UPLOAD_FILE_BYTES } from '../types/upload-file-types.js';
// ─── Grammar / spelling check ────────────────────────────────────────────────
export const grammarCheckSchema = z.object({
    text: z.string().min(1).max(20000),
    /**
     * The voice the correction should keep. Validated as a plain string rather
     * than an enum of the package's tones: an unrecognised value falls back to
     * the default on the server, and pinning the list here would mean a shared
     * release every time the package learns a tone.
     */
    tone: z.string().max(32).optional(),
});
// ─── Translation ─────────────────────────────────────────────────────────────
export const translateSchema = z.object({
    text: z.string().min(1).max(20000),
    targetLanguage: z.string().min(1).max(64),
    sourceLanguage: z.string().min(1).max(64).optional(),
    tone: z.string().max(32).optional(),
});
// ─── Generic email composer ──────────────────────────────────────────────────
export const emailRecipientSchema = z.object({
    email: z.string().email().max(320),
    name: z.string().max(255).nullable().optional(),
});
export const emailAttachmentRefSchema = z.object({
    s3Key: z.string().min(1).max(1024),
    fileName: z.string().min(1).max(512),
    mimeType: z.string().min(1).max(255),
});
/**
 * Asking a recipient for a file back.
 *
 * ⚠️ **A field absent from a schema is deleted, not rejected.** `validate`
 * assigns `req.body = result.data` and Zod strips unknown keys, so a field the
 * client sends and the schema does not name never reaches the controller — and
 * nothing anywhere reports it. That is exactly how the vendor email's upload
 * link, its cc-self and its manually-typed recipients were all silently inert:
 * the controller read them, the schema had never been told about them.
 *
 * So this lives beside the two schemas that accept it, and both name it.
 */
/**
 * A `file-client` node id — `event:<uuid>`, `folder:<uuid>`.
 *
 * ⚠️ **Not a uuid, and this field carries both kinds.** A vendor email files
 * into `document_folders` and names a folder by its bare uuid; a conversation
 * files into the library and names a *node*. One schema serves both, so
 * demanding a uuid refused every conversation's ask with a bare "Validation
 * error" — before the controller that understands node ids ever ran, which is
 * precisely the failure `vendorUploadCreateSchema` records a few lines below.
 *
 * Shape only. Which tree the id belongs to, and whether the sender may file
 * there, is decided server-side — `isFolderUnderRecord` for a conversation,
 * `isFolderWithin` for the email — because a folder id in a request body is
 * never a control.
 */
const LIBRARY_NODE_ID = /^[a-z][a-z0-9_-]{0,31}:[A-Za-z0-9_-]{1,128}$/;
export const uploadLinkConfigSchema = z.object({
    folderId: z
        .union([z.string().uuid(), z.string().regex(LIBRARY_NODE_ID)])
        .nullable()
        .optional(),
    // Registry keys, not MIME types. Unknown keys are dropped server-side rather
    // than refused, so a client built against a newer registry cannot fail a send.
    fileTypes: z.array(z.string().max(32)).max(10).optional(),
    label: z.string().max(255).nullable().optional(),
    // A value of the `document_type` Settings list — "coi", "contract" — the file
    // is tagged with when it arrives. Not checked against the list here: the list
    // is the office's and changes; an unknown value simply tags nothing.
    documentType: z.string().trim().max(64).nullable().optional(),
    // Deliberately no `entityType`/`entityId`: the record a file attaches to is
    // the server's to decide. Accepting it here let a caller aim a guest's upload
    // at any record they could name, checked for shape and nothing else.
});
export const composeEmailSchema = z.object({
    recipients: z.array(emailRecipientSchema).min(1).max(200),
    subject: z.string().min(1).max(500),
    bodyHtml: z.string().min(1).max(100000),
    ccSelf: z.boolean().optional(),
    attachments: z.array(emailAttachmentRefSchema).max(20).optional(),
    context: z.string().max(100).optional(),
    /**
     * Files from the library, named by id.
     *
     * Ids rather than object keys on purpose: the server resolves each one
     * through the file system's own authorizer, so a caller can only attach a
     * file they could already see. A key would have to be guarded by a prefix
     * allowlist instead, and the new library is one prefix holding every
     * client's documents.
     */
    fileIds: z.array(z.string().uuid()).max(50).optional(),
    uploadRequest: uploadLinkConfigSchema.optional(),
});
/**
 * Posting into a conversation.
 *
 * ⚠️ **`uploadRequest` is validated here because the route had no schema at
 * all.** The body was read straight off the request, which was harmless while
 * it held one string — and stops being harmless the moment it carries a folder
 * id and a label that reach the database. `uploadLinkConfigSchema` is the same
 * one the standalone ask uses, so the two cannot drift.
 */
export const threadMessageSchema = z.object({
    body: z.string().max(20000).optional(),
    uploadRequest: uploadLinkConfigSchema.optional(),
});
// ─── Public vendor upload ────────────────────────────────────────────────────
/**
 * ⚠️ **One of `s3Key` and `fileId`, never neither.** The type has said so since
 * a conversation's link started presigning through the file library, and this
 * schema went on requiring `s3Key` — so every upload from a conversation was
 * refused by the validator, before the controller that knows about `fileId`
 * ever ran, and the guest read "Validation error" on a link that could never
 * have worked.
 *
 * Both are still optional individually, because which one arrives is a fact
 * about how the link was issued and the page echoes back whatever it was given.
 * The refinement is what makes "neither" an error rather than a request the
 * controller has to answer for.
 */
const uploadedFileSchema = z
    .object({
    s3Key: z.string().min(1).max(1024).optional(),
    fileId: z.string().min(1).max(128).optional(),
    fileName: z.string().min(1).max(512),
    mimeType: z.string().min(1).max(255),
    fileSizeBytes: z.number().int().min(0).max(MAX_UPLOAD_FILE_BYTES).optional(),
})
    .refine((value) => value.s3Key !== undefined || value.fileId !== undefined, {
    message: 'An upload must say where its bytes went.',
    path: ['s3Key'],
});
/** One file, its fields at the top level — what a page before Phase 9 sends. */
export const vendorUploadCreateSchema = uploadedFileSchema;
/**
 * Finalizing a submission (Phase 9): `files`, 1–`MAX_UPLOAD_FILES` of them, or
 * the single file a page loaded before Phase 9 sends. Both are answered as
 * `{ files }`, so the controller reads one shape.
 */
export const uploadFinalizeSchema = z.union([
    z.object({ files: z.array(uploadedFileSchema).min(1).max(MAX_UPLOAD_FILES) }),
    uploadedFileSchema.transform((file) => ({ files: [file] })),
]);
/** `POST /upload-requests` — a producer asks people for files about a record. */
export const createUploadRequestsSchema = z.object({
    entityType: z.enum(UPLOAD_REQUEST_TARGET_TYPES),
    entityId: z.string().uuid(),
    recipients: z
        .array(z.object({
        email: z.string().trim().email().max(320),
        name: z.string().trim().max(255).nullable().optional(),
    }))
        .min(1)
        .max(20),
    message: z.string().max(5000).nullable().optional(),
    uploadLink: uploadLinkConfigSchema,
});
// ─── Saved replies ───────────────────────────────────────────────────────────
export const savedReplySchema = z.object({
    name: z.string().trim().min(1).max(120),
    subject: z.string().max(255).nullable().optional(),
    body: z.string().min(1).max(20000),
    isShared: z.boolean().optional(),
});
export const updateSavedReplySchema = savedReplySchema.partial();
const mergeRecordSchema = z.object({
    entityType: z.string().min(1).max(64),
    entityId: z.string().uuid(),
});
/**
 * Fill a saved reply against the records the message is about — usually one
 * (the event), sometimes two (the event and the one vendor it goes to). None
 * still fills the writer's own tags. Where two records answer the same tag the
 * first one listed wins.
 */
export const renderSavedReplySchema = z.object({
    records: z.array(mergeRecordSchema).max(5).optional(),
});
// ─── Mail log: suppressed addresses ──────────────────────────────────────────
/** An administrator stopping mail to an address by hand. */
export const addMailSuppressionSchema = z.object({
    address: z.string().trim().email().max(320),
    detail: z.string().trim().max(500).optional(),
});
//# sourceMappingURL=messaging.js.map